/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericEndpointEditorComponent" />
import * as React from "react";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { GenericAuthPickerComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericAuthPickerComponent";
import { GenericAuthorizationComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericAuthorizationComponent";
import { GenericEndpointsDetailsComponents } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericEndpointsDetailsComponents";
import { IEndpointEditorComponentProps } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import { ServiceEndpointsSource } from "DistributedTaskUI/ServiceEndpoints/Editor/Source/ServiceEndpointsSource";
import { EndpointsEditorPanelUtils } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/EndpointsEditorPanelUtils";
import { ServiceEndpointRequest, ServiceEndpointRequestResult } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";

export interface IGenericEndpointEditorComponentProps extends IEndpointEditorComponentProps {
    isVerifiableConnection: boolean;
    updateError: (isError: boolean, errorMessage: string, errorStatus?: string) => void;
    getInvalidInputCount: () => number;
    errorMessage: string;
}

export interface IGenericEndpointEditorComponentState {
    isVerifyInProgress: boolean;
    isVerificationSuccess: boolean;
}

export class GenericEndpointEditorComponent extends VssComponent<IGenericEndpointEditorComponentProps, IGenericEndpointEditorComponentState>  {

    constructor(props: IGenericEndpointEditorComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._source = ServiceEndpointsSource.getInstance(context.pageContext);
    }

    public render(): React.ReactNode {
        return (
            <div>
                <GenericAuthPickerComponent
                    serviceEndpointType={this.props.serviceEndpointType}
                    serviceEndpoint={this.props.draftEndpoint}
                    callbackToSaveState={this.props.updateDraftEndpointState}
                />
                <GenericAuthorizationComponent
                    disableAllPipelinesSetting = {this.props.disableAllPipelinesSetting}
                    serviceEndpointType={this.props.serviceEndpointType}
                    draftEndpoint={this.props.draftEndpoint}
                    isUpdateForm={this.props.isUpdateForm}
                    updateDraftEndpointState={this.props.updateDraftEndpointState}
                    updateInvalidInputsCount={this.props.updateInvalidInputsCount}
                />
                {/* This verificationcomponent is being rendered in other endpoint components as well. Ensure to make similar changes at other places if this is updated */}
                { this._getVerificationComponent() }
                <GenericEndpointsDetailsComponents
                    serviceEndpoint={this.props.draftEndpoint}
                    isUpdateForm={this.props.isUpdateForm}
                    updateDraftEndpointState={this.props.updateDraftEndpointState}
                    updateInvalidInputsCount={this.props.updateInvalidInputsCount}
                    updateAllowAllPipelinesFlag={this.props.updateAllowAllPipelinesFlag}
                    allowAllPipelinesFlagState={this.props.allowAllPipelinesFlagState}
                    disableAllPipelinesSetting={this.props.disableAllPipelinesSetting}
                />
            </div>
        );
    }

    private _getVerificationComponent(): React.ReactNode {
        if (this.props.isVerifiableConnection) {
            const isVerificationDisabled: boolean = (this.state && this.state.isVerifyInProgress) || this.props.getInvalidInputCount() > 1 || (this.props.getInvalidInputCount() === 1 && !!this.props.draftEndpoint.name && this.props.draftEndpoint.name.trim().length > 0);
            return (
                <div className={"flex-row default-input-margin-top"}>
                    { EndpointsEditorPanelUtils.getVerifyButton(isVerificationDisabled, this._verifyEndpoint) }
                    { this.state && EndpointsEditorPanelUtils.getVerificationDetails(this.state.isVerifyInProgress, this.state.isVerificationSuccess, this.props.errorMessage) }
                </div>
            );
        } else {
            return null;
        }
    }

    private _verifyEndpoint = (): void => {
        this._updateVerificationStatus(true, undefined);
        this._verifyConnection().then((serviceEndpointRequestResult: ServiceEndpointRequestResult) => {
            if (serviceEndpointRequestResult.errorMessage) {
                this._updateVerificationStatus(false, false);
                this.props.updateError(true, serviceEndpointRequestResult.errorMessage, Resources.VerificationFailed);
            } else {
                this._updateVerificationStatus(false, true);
                this.props.updateError(false, "");
            }
        }).catch((error) => {
            this._updateVerificationStatus(false, false);
            const errorMessage: string = error.message ? error.message : Resources.ValidateEndpointErrorMessage;
            this.props.updateError(true, errorMessage, Resources.VerificationFailed);
        });
    }

    private _verifyConnection(): Promise<ServiceEndpointRequestResult> {
        const serviceEndpointRequest: ServiceEndpointRequest = EndpointsEditorPanelUtils.getServiceEndpointRequestForTest(this.props.draftEndpoint);
        return this._source.verifyServiceEndpoint(serviceEndpointRequest, this.props.draftEndpoint.id);
    }

    private _updateVerificationStatus = (isVerifyInProgress: boolean, isVerificationSuccess: boolean): void => {
        this.setState({
            isVerifyInProgress: isVerifyInProgress,
            isVerificationSuccess: isVerificationSuccess
        });
    }

    private _source: ServiceEndpointsSource;
}

VssComponent.register("endpoint-editor-component-generic", GenericEndpointEditorComponent);
